import moment from 'moment-timezone';

import Transaction from '../Entities/Transaction';
import { convertDateToUTC } from './date';
import { queryListInvoices } from '../Queries';
import { queryListCreditEntries } from '../Queries/Credit';
import { queryListPayments, queryListReversals } from '../Queries/Payment';
import Invoice from '../Entities/Invoice';
import { getFacility } from './facility';
import featureFlags from '../featureFlags';

export const paymentFields = [
  'id',
  'type',
  'amount',
  'created_at',
  'payment_meta',
  'credit_entry_id',
  'notes',
  {'invoice': [
    'id',
    'readable_id',
    'balance',
    'type',
  ]},
  {'credit_entry': [
    'created_at',
    'application_date',
    'type',
    'amount',
    'credit_id',
    {'credit': [
      'id',
      'balance',
      'subscription_id',
      'readable_id',
      'payment_type',
      'application_type',
    ]},
  ]},
  {'line_item_payments': [
    {'payment_entry': [
      'type',
      {'transaction': [
        'id',
        'invoice_number',
        'type',
        'payment_type',
        'payment_id',
        'amount',
        'notes',
        'created_at',
        'application_date',
        'invoice_id',
        'subscription_id',
        'credit_entry_id',
      ]},
    ]},
  ]},
];
export const paymentQueryFields = [
  'total',
  'limit',
  'offset',
  {'payments': [
      ...paymentFields
    ]},
];

const reversalQueryFields = [
  'total',
  'limit',
  'offset',
  {'reversals': [
      'id',
      'type',
      'amount',
      'created_at',
      'payment_meta',
      'credit_entry_id',
      'notes',
      {'invoice': [
          'id',
          'readable_id',
          'balance',
          'type',
        ],
      },
      {'line_item_payments': [
          {'reversal_entry': [
              'id',
              'type',
              {'transaction': [
                  'id',
                  'invoice_number',
                  'type',
                  'payment_type',
                  'payment_id',
                  'amount',
                  'notes',
                  'created_at',
                  'application_date',
                  'invoice_id',
                  'subscription_id',
                  'credit_entry_id',
                ]},
            ]},
        ]},
      {'credit_entry': [
          'created_at',
          'application_date',
          'type',
          'amount',
          'credit_id',
          {'credit': [
              'id',
              'balance',
              'subscription_id',
              'readable_id',
              'payment_type',
              'application_type',
            ]},
        ]},
    ]},
];

const invoiceQueryFields = [
  'total',
  'limit',
  'offset',
  {'invoices': [
    'id',
    'readable_id',
    'amount',
    'balance',
    'created_at',
    'type',
    'notes',
    {'line_items': [
      'id',
      'balance',
      'adjusted_gross_amount',
      'adjusted_gross_amounts',
      {'entries': [
        'id',
        'line_item_id',
        'type',
        'amount',
        'amount_tax_1',
        'amount_tax_2',
        'amount_tax_3',
        'amount_tax_4',
        'gross_amount',
        'created_at',
        {'transaction': [
          'id',
          'notes',
          'invoice_id',
          'line_item_entry_id',
          'created_at',
          'application_date'
        ]},
      ]},
    ]},
  ]},
];

const creditEntryQueryFields = [
  {'credit_entries': [
    'created_at',
    'application_date',
    'type',
    'amount',
    {'credit': [
      'id',
      'balance',
      'subscription_id',
      'readable_id',
      'payment_type',
      'application_type',
      {'payment': [
        'id',
        'type',
        'amount',
        'created_at',
        'payment_meta',
        'credit_entry_id',
        'notes',
        {'invoice': [
          'id',
          'readable_id',
          'type',
          'notes',
        ]},
      ]},
    ]},
  ]},
];

export class PaymentHistory {
  subscriptionId = null;
  invoiceId = null;
  facilityId;

  fieldConfiguration = {
    transaction: null,
    invoice: null,
    facilityTimezone: null,
  };

  filters = {
    invoice_number: null,
    start_date: null,
    end_date: null,
    show_unpaid_invoices: null,
  };

  results = {
    payments: [],
    reversals: [],
    credits: [],
    invoices: [],
  };

  constructor(args) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    this.facilityId = urlSearchParams.get('facility');

    this.subscriptionId = args?.subscriptionId;
    this.invoiceId = args?.invoiceId;
    if (args.filters) {
      this.filters = {...this.filters, ...args?.filters};
    }
    if (args.fieldConfiguration) {
      this.fieldConfiguration = {...args.fieldConfiguration};
    }
  }

  async initialize() {
    this.fieldConfiguration.transaction = await Transaction.getFieldAllowedValues(['type', 'payment_type']);
    this.fieldConfiguration.invoice = await Invoice.getFieldAllowedValues(['type']);
    const facility = await getFacility(this.facilityId);
    this.fieldConfiguration.facilityTimezone = facility.fields.timezone.value;
  }

  getQueryFilters() {
    const { facilityTimezone } = this.fieldConfiguration;

    const validTypes = Object.keys(Transaction.getTypeEnumForHistoryReports());

    let requireInvoice = true;

    const queryFilters = [
      {
        model: 'TRANSACTION',
        field: 'type',
        op: 'IN',
        value: validTypes
      }
    ];

    if (this.subscriptionId) {
      queryFilters.push({
        model: 'INVOICE',
        field: 'subscription_id',
        op: 'EQ',
        value: this.subscriptionId
      });
    }
    if (this.invoiceId) {
      queryFilters.push({
        model: 'INVOICE',
        field: 'id',
        op: 'EQ',
        value: this.invoiceId
      });
      requireInvoice = true;
    }

    for (const filter in this.filters) {
      if (this.filters[filter] === null) continue;

      switch (filter) {
        case 'invoice_number':
          queryFilters.push({
            model: 'INVOICE',
            field: 'readable_id',
            op: 'EQ',
            value: this.filters.invoice_number
          });
          requireInvoice = true;
          break;
        case 'start_date':
          const startDate = moment(this.filters.start_date, 'YYYY-MM-DDTHH:mm').tz(facilityTimezone).utc();
          queryFilters.push({
            model: 'PAYMENT',
            field: 'created_at',
            op: 'GTE',
            value: startDate.format()
          });
          requireInvoice = true;
          break;
        case 'end_date':
          const endMoment = moment(this.filters.end_date);
          endMoment.add(1, 'days');
          const endDate = moment(endMoment, 'YYYY-MM-DDTHH:mm').tz(facilityTimezone).utc();
          queryFilters.push({
            model: 'PAYMENT',
            field: 'created_at',
            op: 'LT',
            value: endDate.format(),
          });
          requireInvoice = true;
          break;
        case 'show_unpaid_invoices':
          const invoiceTypes = this.fieldConfiguration.invoice.type.options.map(option => option.value);
          queryFilters.push(
            {
              model: 'INVOICE',
              field: 'type',
              op: 'IN',
              value: invoiceTypes
            },
            {
              model: 'INVOICE',
              field: 'balance',
              op: 'GT',
              value: 0
            }
          );
          requireInvoice = true;
          break;
        default:
      }
    }

    return { filters: queryFilters, requireInvoice };
  }

  async fetchInvoices() {
    const { facilityTimezone } = this.fieldConfiguration;

    const validTypes = Object.keys(Transaction.getTypeEnumForHistoryReports());

    const queryFilters = [
      {
        model: 'TRANSACTION',
        field: 'type',
        op: 'IN',
        value: validTypes
      },
      {
        model: 'LINE_ITEM_ENTRY',
        field: 'type',
        op: 'IN',
        value: ['CHARGE', 'ADJUSTMENT', 'POSITIVE_ADJUSTMENT', 'WRITE_OFF', 'LANDLORD_COLLECTED'],
      },
    ];

    if (this.subscriptionId) {
      queryFilters.push({
        model: 'INVOICE',
        field: 'subscription_id',
        op: 'EQ',
        value: this.subscriptionId
      });
    }
    if (this.invoiceId) {
      queryFilters.push({
        model: 'INVOICE',
        field: 'id',
        op: 'EQ',
        value: this.invoiceId
      });
    }

    for (const filter in this.filters) {
      if (this.filters[filter] === null) continue;

      switch (filter) {
        case 'invoice_number':
          queryFilters.push({
            model: 'INVOICE',
            field: 'readable_id',
            op: 'EQ',
            value: this.filters.invoice_number
          });
          break;
        case 'start_date':
          const startDate = moment(this.filters.start_date, 'YYYY-MM-DDTHH:mm').tz(facilityTimezone).utc();
          queryFilters.push({
            model: 'INVOICE',
            field: 'created_at',
            op: 'GTE',
            value: startDate.format(),
          });
          break;
        case 'end_date':
          const endMoment = moment(this.filters.end_date);
          endMoment.add(1, 'days');
          const endDate = moment(endMoment, 'YYYY-MM-DDTHH:mm').tz(facilityTimezone).utc();
          queryFilters.push({
            model: 'INVOICE',
            field: 'created_at',
            op: 'LT',
            value: endDate.format(),
          });
          break;
        case 'show_unpaid_invoices':
          const invoiceTypes = this.fieldConfiguration.invoice.type.options.map(option => option.label);
          queryFilters.push(
            {
              model: 'INVOICE',
              field: 'type',
              op: 'IN',
              value: invoiceTypes
            },
            {
              model: 'INVOICE',
              field: 'balance',
              op: 'GT',
              value: 0
            }
          );
          break;
        default:
      }
    }

    const options = { filters: queryFilters };

    try {
      const response = await queryListInvoices(options, invoiceQueryFields);
      if(response.errors) {
        throw (response.errors.map(error => error.message));
      }

      return response.data.listInvoices.invoices;
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [ errors ];
    }
  }

  async fetchPayments() {
    const options = this.getQueryFilters();

    try {
      const response = await queryListPayments(options, paymentQueryFields);
      if(response.errors) {
        throw (response.errors.map(error => error.message));
      }

      return response.data.listPayments.payments;
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [ errors ];
    }
  }

  async fetchReversals() {
    const options = this.getQueryFilters();

    options.filters.push({
      model: 'LINE_ITEM_ENTRY',
      field: 'type',
      op: 'IN',
      value: ['REVERSAL', 'UNAPPLY_CREDIT'],
    });

    try {
      const response = await queryListReversals(options, reversalQueryFields);
      if(response.errors) {
        throw (response.errors.map(error => error.message));
      }

      return response.data.listReversals.reversals;
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [ errors ];
    }
  }

  async fetchCreditEntries() {
    const { facilityTimezone } = this.fieldConfiguration;

    if (this.filters.show_unpaid_invoices) {
      return [];
    }

    // 'Creation','Increase','Overpayment','Payment','Restoration','Reversal','Write Off'
    const excludeTypes = ['PAYMENT'];

    const options = {
      filters: [],
    };

    if (this.subscriptionId) {
      options.filters.push({
        model: 'CREDIT',
        field: 'subscription_id',
        op: 'EQ',
        value: this.subscriptionId
      });
    }

    if (this.invoiceId) {
      options.filters.push({
        model: 'INVOICE',
        field: 'id',
        op: 'EQ',
        value: this.invoiceId
      });

      // Since this is for the Invoice Transaction History we want to exclude increases, and write-offs.
      // Payments are included via the Payments query.
      excludeTypes.push('INCREASE', 'WRITE_OFF');
    }

    options.filters.push({
      model: 'CREDIT_ENTRY',
      field: 'type',
      op: 'NOT_IN',
      value: excludeTypes,
    });

    for (const filter in this.filters) {
      if (this.filters[filter] === null) continue;

      switch (filter) {
        case 'start_date':
          const startDate = moment(this.filters.start_date, 'YYYY-MM-DDTHH:mm').tz(facilityTimezone).utc();
          options.filters.push({
            model: 'CREDIT_ENTRY',
            field: 'created_at',
            op: 'GTE',
            value: startDate
          });
          break;
        case 'end_date':
          const endMoment = moment(this.filters.end_date);
          endMoment.add(1, 'days');
          const endDate = moment(endMoment, 'YYYY-MM-DDTHH:mm').tz(facilityTimezone).utc();
          options.filters.push({
            model: 'CREDIT_ENTRY',
            field: 'created_at',
            op: 'LT',
            value: endDate.format()
          });
          break;
        default: break;
      }
    }

    try {
      const response = await queryListCreditEntries(
        options,
        creditEntryQueryFields,
      );
      if(response.errors) {
        throw (response.errors.map(error => error.message));
      }

      return response.data.listCreditEntries.credit_entries;
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [ errors ];
    }
  }

  async fetchAll() {
    // Collect data from queries
    const results = await Promise.all([
      this.fetchPayments(),
      this.fetchReversals(),
      this.fetchCreditEntries(),
      this.fetchInvoices(),
    ]);

    this.results = {
      payments: results[0],
      reversals: results[1],
      creditEntries: results[2],
      invoices: results[3],
      adjustments: PaymentHistory.getAdjustments(results[3]),
    };

    return this.results;
  }

  static collectDataRows(results, useInvoiceBalance = false) {
    const rows = [];
    results.payments.forEach((payment) => {
      const entry = payment.line_item_payments[0]?.payment_entry;
      rows.push({
        model: 'PAYMENT',
        id: payment.id,
        paymentId: payment.id,
        timestamp: payment.created_at,
        applicationDate: payment?.credit_entry?.application_date ?? entry?.transaction?.application_date,
        appType: payment?.credit_entry?.credit?.application_type,
        type: entry?.type,
        invoiceNumber: payment?.invoice?.readable_id,
        invoiceId: payment?.invoice?.id,
        creditId: payment?.credit_entry?.credit?.id,
        creditNumber: payment?.credit_entry?.credit?.readable_id,
        paymentMeta: payment.payment_meta,
        creditAmount: null,
        amount: PaymentHistory.maybeNegate(payment.amount, entry?.type),
        balance: null,
        notes: payment.notes,
      });
    });
    results.reversals.forEach((payment) => {
      const entry = payment.line_item_payments[0]?.reversal_entry;
      rows.push({
        model: 'REVERSAL',
        id: payment.id,
        paymentId: payment.id,
        timestamp: payment.created_at,
        applicationDate: payment?.credit_entry?.application_date ?? entry?.transaction?.application_date,
        appType: payment?.credit_entry?.credit?.application_type,
        type: entry?.type,
        invoiceNumber: payment?.invoice?.readable_id,
        invoiceId: payment?.invoice?.id,
        creditId: payment?.credit_entry?.credit?.id,
        creditNumber: payment?.credit_entry?.credit?.readable_id,
        paymentMeta: payment.payment_meta,
        creditAmount: null,
        amount: PaymentHistory.maybeNegate(payment.amount, entry?.type),
        balance: null,
        notes: payment.notes,
      });
    });
    results.creditEntries.forEach((creditEntry) => {
      const payment = creditEntry.payment ?? creditEntry.credit.payment;
      rows.push({
        model: 'CREDIT',
        id: creditEntry.id,
        paymentId: payment?.invoice?.id,
        timestamp: creditEntry.created_at,
        applicationDate: creditEntry.application_date,
        appType: creditEntry.credit.application_type,
        type: creditEntry.type,
        invoiceNumber: payment?.invoice?.readable_id,
        invoiceId: payment?.invoice?.id,
        creditId: creditEntry.credit.id,
        creditNumber: creditEntry.credit.readable_id,
        paymentMeta: null,
        creditAmount: PaymentHistory.maybeNegate(creditEntry.amount, `Credit - ${creditEntry.type}`),
        amount: null,
        balance: null,
        notes: payment?.notes,
      });
    });
    results.invoices.forEach((invoice) => {
      const chargeAmount = PaymentHistory.getInvoiceCharge(invoice);
      rows.push({
        model: 'INVOICE',
        id: invoice.id,
        paymentId: null,
        timestamp: invoice.created_at,
        applicationDate: null,
        appType: null,
        type: invoice.type,
        invoiceNumber: invoice.readable_id,
        invoiceId: invoice.id,
        creditId: null,
        creditNumber: null,
        paymentMeta: null,
        creditAmount: null,
        amount: chargeAmount,
        balance: useInvoiceBalance ? invoice.balance : chargeAmount,
        notes: invoice.notes,
      });
    });
    results.adjustments.forEach((entry) => {
      rows.push({
        model: 'LINE_ITEM_ENTRY',
        id: entry.id,
        paymentId: entry.id,
        timestamp: entry.created_at,
        applicationDate: null,
        type: entry.type,
        invoiceNumber: entry.invoice_number,
        invoiceId: entry.invoice_id,
        creditId: null,
        creditNumber: null,
        paymentMeta: null,
        creditAmount: null,
        amount: PaymentHistory.maybeNegate(entry.gross_amount, entry.type),
        balance: null,
        notes: entry.transaction.notes,
      });
    });
    return rows;
  }

  /**
   * Negate an amount if it has a type that decreases an invoice balance.
   * @param value
   * @param type
   * @returns {number}
   */
  static maybeNegate(value, type) {
    let n = value;
    if (typeof n === 'string') {
      n = parseInt(n);
    }

    const normalizedType = PaymentHistory.normalizeType(type);

    if ([
      'PAYMENT',
      'CREDIT__PAYMENT',
      'ADJUSTMENT',
      'LANDLORD_COLLECTED',
      'WRITE_OFF',
      'CREDIT__WRITE_OFF',
      'CREDIT__REVERSAL',
    ].includes(normalizedType)) {
      // decrease, negative
      if (n > 0) {
        n *= -1;
      }
    } else if (n < 0) {
      // increase, positive
      n *= -1;
    }

    return n;
  }

  /**
   * Convert a number into a dollar sign amount where negative values are surrounded by parentheses.
   * @param value
   * @returns {null|string}
   */
  static formatCurrency = (value) => {
    if (value !== null) {
      let neg = false;
      let n = value;
      if (value < 0) {
        neg = true;
        n *= -1;
      }
      const localeStr = (n / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
      return `${neg ? '(' : ''}${localeStr}${neg ? ')' : ''}`;
    }
    return null;
  }

  /**
   * Gets the adjustment entries from a given invoice.
   * @param invoices
   * @returns {*[]}
   */
  static getAdjustments(invoices) {
    const types = ['ADJUSTMENT', 'POSITIVE_ADJUSTMENT', 'WRITE_OFF', 'LANDLORD_COLLECTED'];
    const adjustments = [];
    invoices.forEach((invoice) => {
      invoice.line_items.forEach((lineItem) => {
        lineItem.entries.forEach((entry) => {
          const type = PaymentHistory.normalizeType(entry.type);
          if (types.includes(type)) {
            adjustments.push({
              ...entry,
              invoice_id: invoice.id,
              invoice_number: invoice.readable_id,
            });
          }
        });
      });
    });
    return adjustments;
  }

  /**
   * Get the total charge amount from the line item entries of an invoice.
   * @param invoice
   * @returns number
   */
  static getInvoiceCharge(invoice) {
    return invoice.line_items.reduce((amount, lineItem) => lineItem.entries.reduce((chargeTotal, entry) => {
      if (entry.type === 'CHARGE') {
        return chargeTotal + entry.gross_amount;
      }
      return chargeTotal;
    }, amount), 0);
  }

  /**
   * Returns a normalized string uppercase, with dashes removed and spaces replaced by underscores.
   * @param type
   * @returns {string|null}
   */
  static normalizeType(type) {
    if (!type) return null;
    return type.toUpperCase()
      .replaceAll(' ', '_')
      .replaceAll('-', '');
  }

  /**
   * Convert a transaction/entry/invoice/credit type for display across the app.
   * @param applicationType
   * @param type
   * @returns {*|string}
   */
  static getType(applicationType, type) {
    const appTypeUpper = applicationType ? this.normalizeType(applicationType) : null;
    const typeUpper = type ? this.normalizeType(type) : null;

    switch (typeUpper) {
      case 'PAYMENT': // decreases balance
        if (appTypeUpper) {
          switch (appTypeUpper) {
            case 'CARRYOVER_CREDIT':
              return 'Payment - Carryover Credit Applied';
            case 'APPLY_CASH':
              return 'Payment - Credit No Cash Applied';
            case 'DISCOUNT_CREDIT':
              return 'Payment - Discount Credit Applied';
            default:
              return 'Payment - Credit Cash Applied';
          }
        }
        return type;
      case 'REVERSAL': // increases balance
        if (appTypeUpper) {
          return 'Reversal - Credit Payment';
        }
        return 'Reversal - Payment';
      case 'UNAPPLY_CREDIT': // increases balance
        switch (appTypeUpper) {
          case 'CARRYOVER_CREDIT':
            return 'Unapply - Carryover Credit';
          case 'APPLY_CASH':
            return 'Unapply - Credit No Cash Applied';
          case 'DISCOUNT_CREDIT':
            return 'Unapply - Discount Credit';
          default:
            return 'Unapply - Credit Cash Applied';
        }
      // CREDITS
      case 'CREDIT__INCREASE':
        switch (appTypeUpper) {
          case 'APPLY_CASH':
            return 'Credit Created - Cash Applied';
          case 'DO_NOT_APPLY_CASH':
            return 'Credit Created - Cash Not Applied';
          case 'CARRYOVER_CREDIT':
            return 'Credit - Carryover Credit Increase';
          case 'DISCOUNT_CREDIT':
            return 'Credit - Discount Credit Increase';
          default:
            return 'Credit Created';
        }
      case 'CREDIT__CREATION':
      case 'CREDIT__OVERPAYMENT':
        switch (appTypeUpper) {
          case 'APPLY_CASH':
            return 'Credit Created - Cash Applied';
          case 'DO_NOT_APPLY_CASH':
            return 'Credit Created - Cash Not Applied';
          case 'CARRYOVER_CREDIT':
            return 'Credit Created - Carryover Credit';
          case 'DISCOUNT_CREDIT':
            return 'Credit Created - Discount Credit';
          default:
            return 'Credit Created';
        }
      case 'CREDIT__PAYMENT': // decreases balance
        return 'Credit - Used for Payment';
      case 'CREDIT__REVERSAL': // increases balance
        switch (appTypeUpper) {
          case 'APPLY_CASH':
            return 'Reversal - Credit Cash Applied';
          case 'DO_NOT_APPLY_CASH':
            return 'Reversal - Credit No Cash Applied';
          default:
            return type;
        }
      case 'CREDIT__UNAPPLY_CREDIT': // increases balance
      case 'CREDIT__RESTORATION':
        switch (appTypeUpper) {
          case 'CARRYOVER_CREDIT':
            return 'Credit - Carryover Credit Restored';
          case 'DISCOUNT_CREDIT':
            return 'Credit - Discount Credit Restored';
          default:
            return 'Credit - Restored';
        }
      case 'CREDIT__WRITE_OFF':
        switch (appTypeUpper) {
          case 'APPLY_CASH':
            return 'Credit Write Off - Cash Applied';
          case 'DO_NOT_APPLY_CASH':
            return 'Credit Write Off - Cash Not Applied';
          case 'CARRYOVER_CREDIT':
            return 'Credit - Carryover Credit Write Off';
          case 'DISCOUNT_CREDIT':
            return 'Credit - Discount Credit Write Off';
          default:
            return type;
        }
      default: return type;
    }
  }

}